import React from 'react';
import clsx from 'clsx';
import { useSelector } from 'react-redux';
import { makeStyles, Theme } from '@material-ui/core/styles';
import get from 'lodash/get';
import NamedBox from '../atoms/NamedBox';
import Ranks, { RanksType } from '../atoms/Ranks';
import Loader from '../atoms/Loader';
import Accordion from '../atoms/Accordion';
import { RanksComparisonGroup } from '../molecules/RanksTooltip';
import { FONT_PROXIMA_NOVA } from '../styles/fonts';
import { inchesToFeetAndInches, secondsToMinutesSecondsMs } from '../services/converter';
import { FormatType, getEventFormatType, getEventName } from '../services/event-names-and-formats';
import Position from '../types/Position';
import Player from '../types/Player';
import Team from '../types/Team';
import PlayerCombineEvent from '../types/PlayerCombineEvent';
import College from '../types/College';
import HighSchool from '../types/HighSchool';
import PlayerCombineStats from '../types/PlayerCombineStats';
import PlayerNFLCombineEvent from '../types/PlayerNFLCombineEvent';
import PlayerNFLCombineStats from '../types/PlayerNFLCombineStats';
import { formatDate } from '../services/player';
import Score, { SCORE_TYPE } from '../atoms/Score';
import Tooltip from '../atoms/Tooltip';
import FlagIcon from '../icons/FlagIcon';
import { COLOR_BACKGROUND_LIGHT, COLOR_BORDER, COLOR_GREEN } from '../styles/colors';
import Column from '../types/Column';
import { CHARTS_COLUMN } from '../types/ChartsColumn';
import {
  DEFAULT_PROFILE_STATS_COLUMNS,
  getCollegeHeaderItemByColumn,
  getNflHeaderItemByColumn,
  getProfileStatsComparisonRankValue
} from '../services/profile-stats-categories';
import { State } from '../redux/reducers';


interface PlayerCombineDataProps {
  className?: string;
  loading?: boolean;
  player?: Player;
  collegeTeam?: Team;
  nflTeam?: Team;
  position?: Position;
  combineEvents: Array<PlayerCombineEvent | PlayerNFLCombineEvent>;
  columns?: Column[];
  isNFL?: boolean;
  hideHeader?: boolean;
}

const MIN_EVENT_YEAR = 2010;

const useStyles = makeStyles((theme: Theme) => ({
  playerCombineData: {
    borderBottom: 0,
    position: 'relative',
  },

  header: {
    marginBottom: theme.spacing(2),
  },

  event: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    alignItems: 'center',
    padding: theme.spacing(2),
  },
  eventLogo: {
    width: 64,
    height: 64,
  },
  eventInfo: {
    flexGrow: 1,
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
    minHeight: 78,
    borderRadius: 10,
    backgroundColor: COLOR_BACKGROUND_LIGHT,
    fontFamily: FONT_PROXIMA_NOVA,

    '@media (max-width: 1260px)': {
      display: 'grid',
      gridTemplateColumns: 'repeat(4, 1fr)',
      gap: theme.spacing(1),
      minHeight: 0,
      borderRadius: 0,
      backgroundColor: 'transparent',
    },

    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: 'repeat(2, 1fr)',
    },
  },
  eventInfoRow: {
    flexGrow: 1,
    flexBasis: '15%',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(1),
    textAlign: 'center',
    borderRight: `1px solid ${COLOR_BORDER}`,

    '&:last-child': {
      borderRight: 0,
    },

    '@media (max-width: 1260px)': {
      border: 0,
      borderRadius: 10,
      backgroundColor: COLOR_BACKGROUND_LIGHT,
    },
  },
  eventInfoName: {
    fontSize: theme.typography.pxToRem(12),
    lineHeight: 1,
  },
  eventInfoValue: {
    fontSize: theme.typography.pxToRem(16),
    lineHeight: 1,
    fontWeight: 700,
  },
  hideHeader: {
    display: 'none',
  },
  playerScore: {
    height: 75,
    width: 75,
    margin: '0 auto',
    fontSize: theme.typography.pxToRem(26),
  },
  hsScorePosition: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  hsScorePositionCheckMark: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),

    '& svg': {
      display: 'block',
    },
  },
  freshmanIcon: {
    width: 24,
    height: 24,
    color: COLOR_GREEN,
  },
}), { name: PlayerCombineData.name });

export default function PlayerCombineData (props: PlayerCombineDataProps) {
  const {
    className,
    loading = false,
    player,
    collegeTeam,
    nflTeam,
    position,
    combineEvents,
    columns = [],
    isNFL,
    hideHeader = false
  } = props;
  const classes = useStyles();
  const newProfileEnabled = useSelector((state: State) => Boolean(state.user?.program?.newProfileEnabled));

  function displayPerformance (performance:number, formatType:FormatType):string {
    if (formatType === FormatType.FEET_AND_INCHES) {
      return inchesToFeetAndInches(performance);
    } else if (formatType === FormatType.INCHES) {
      return `${Number(performance).toFixed(2)}"`;
    } else if (formatType === FormatType.MINUTES_SECONDS_MS) {
      return secondsToMinutesSecondsMs(performance);
    }

    return Number(performance).toFixed(2);
  }

  function getRanksEvent (stats:PlayerCombineStats | PlayerNFLCombineStats) {
    return {
      type: getEventName(stats.event),
      name: stats.meet ? `${stats.meet} ${stats.year ? `(${stats.year})` : ''}` : '',
      result: displayPerformance(stats.performance, getEventFormatType(stats.event)),
    };
  }

  function getEventYear (eventDate:string):number {
    return new Date(eventDate).getUTCFullYear();
  }

  function renderCollegeEvents (
    stats:PlayerCombineStats,
    event:PlayerCombineEvent | PlayerNFLCombineEvent,
    index:number,
  ) {
    return (
      <Ranks
        key={index}
        type={RanksType.COMBINE}
        playerId={player && player.id}
        teamId={collegeTeam && collegeTeam.id}
        collegeTeamId={collegeTeam && collegeTeam.id}
        nflTeamId={nflTeam && nflTeam.id}
        position={position}
        comparisonEvent={stats.event}
        combineId={event.id}
        combineCategory={event.category}
        event={getRanksEvent(stats)}
        isNFL={isNFL}
        values={selectedColumns.map((column:Column, columnIndex:number) => {
          const comparisonStats = get(stats, 'comparisonStats', []);
          return getProfileStatsComparisonRankValue({
            column,
            columnIndex,
            comparisonStats,
            isNFL,
            newProfileEnabled,
          });
        })}
        bottomSpace
      />
    );
  }

  function renderNFLEvents (
    stats:PlayerNFLCombineStats,
    event:PlayerCombineEvent | PlayerNFLCombineEvent,
    index:number,
  ) {
    return (
      <Ranks
        key={index}
        type={RanksType.COMBINE}
        playerId={player && player.id}
        teamId={nflTeam && nflTeam.id}
        collegeTeamId={collegeTeam && collegeTeam.id}
        nflTeamId={nflTeam && nflTeam.id}
        position={position}
        comparisonEvent={stats.event}
        combineId={event.id}
        combineCategory={event.category}
        event={getRanksEvent(stats)}
        isNFL={isNFL}
        values={selectedColumns.map((column:Column, columnIndex:number) => {
          const comparisonStats = get(stats, 'comparisonStats', []);
          return getProfileStatsComparisonRankValue({
            column,
            columnIndex,
            comparisonStats,
            isNFL,
            newProfileEnabled,
          });
        })}
        bottomSpace
      />
    );
  }

  const selectedColumns = (columns.filter((column:Column) => column.selected).length
    ? columns.filter((column:Column) => column.selected)
    : DEFAULT_PROFILE_STATS_COLUMNS) as Column[];

  function renderEvent (event:PlayerCombineEvent | PlayerNFLCombineEvent) {
    const eventYear = event?.combineDate ? new Date(event.combineDate).getUTCFullYear() : 0;
    let combines: any = combineEvents?.find(combine => combine?.id == event?.id )?.combine;

    return (
      <div className={classes.event}>
        {(event.type?.toLowerCase().includes('nfl') && event.type?.toLowerCase().includes('combine')) && (
          <img
            className={classes.eventLogo}
            src='/images/nfl-combine.png'
            alt='NFL Scouting Combine'
          />
        )}

        <div className={ clsx(event?.city?.length > 20 && classes.hsScorePositionCheckMark, combines?.length ? classes.hsScorePosition : classes.hsScorePositionCheckMark)}> 
          <Score
            className={classes.playerScore}
            type={SCORE_TYPE.COMBINE}
            position={position}
            scoreList={combines || []}
            showCheckMarkExplict= {!combines?.length || false}
          />

          {
            combineEvents?.find(combine => combine?.id == event?.id )?.combineHasFreshmanData && (
              <Tooltip title='Freshman Data'>
                <FlagIcon className={classes.freshmanIcon} />
              </Tooltip>
            )
          }
        </div>

        <div className={classes.eventInfo}>
          {
          !["Under Armour", "ESPN", "Elite 11"].includes(event.type)
          ? <div className={classes.eventInfoRow}>
              <div className={classes.eventInfoName}>Event</div>
              <div className={classes.eventInfoValue}>{event.type}</div>
            </div>
            : null
          }

          {!!event.city && (
            <div className={classes.eventInfoRow}>
              <div className={classes.eventInfoName}>City</div>
              <div className={classes.eventInfoValue}>{event.city}</div>
            </div>
          )}

          {eventYear >= MIN_EVENT_YEAR && (
            <div className={classes.eventInfoRow}>
              <div className={classes.eventInfoName}>Year</div>
              <div className={classes.eventInfoValue}>{formatDate(event.combineDate, true)}</div>
            </div>
          )}

          {!!event.height && (
            <div className={classes.eventInfoRow}>
              <div className={classes.eventInfoName}>Height</div>
              <div className={classes.eventInfoValue}>{inchesToFeetAndInches(event.height)}</div>
            </div>
          )}

          {!!event.weight && (
            <div className={classes.eventInfoRow}>
              <div className={classes.eventInfoName}>Weight</div>
              <div className={classes.eventInfoValue}>{event.weight} lbs.</div>
            </div>
          )}
        </div>
      </div>
    );
  }
  const college = (player?.playerColleges || [])
    .find((college:College) => college && college.isPrimary);
  const highSchool = (player?.highSchools || [])
    .find((highSchool:HighSchool) => !!highSchool?.isPrimary);
  const recruitingClass = college?.recruitingClass || highSchool?.graduatingClass;

  return (
    <NamedBox
      className={clsx(classes.playerCombineData, className)}
      headerClassName={clsx(classes.header, hideHeader && classes.hideHeader)}
      name={`HS Combine Data - ${position}`}
    >
      <Loader inProgress={loading} />

      {!!combineEvents?.length && (
        combineEvents?.map((event:PlayerCombineEvent | PlayerNFLCombineEvent, index:number) => (
          <Accordion
            key={index}
            summary={`${["Under Armour", "ESPN", "Elite 11"].includes(event.type) ? 'Combine' : event.type }`}
            expanded={index === 0 || index === 1}
          >
            {renderEvent(event)}

            {!isNFL && (
              <Ranks
                headerItems={selectedColumns.map((column:Column) => getCollegeHeaderItemByColumn(column, {
                  recruitingClass,
                  collegeTeam,
                }))}
              />
            )}

            {isNFL && (
              <Ranks
                headerItems={selectedColumns.map((column:Column) => getNflHeaderItemByColumn(column, {
                  collegeTeam,
                  nflTeam,
                }))}
              />
            )}

            {(event.eventStats && !!event.eventStats.length) && (
              (event.eventStats as any[])
                .filter((stats:any) => !!stats.performance)
                .map((stats:any, index:number) => (
                  isNFL
                    ? renderNFLEvents(stats as PlayerNFLCombineStats, event, index)
                    : renderCollegeEvents(stats as PlayerCombineStats, event, index)
                  )
                )
            )}
          </Accordion>
        ))
      )}
    </NamedBox>
  );
}
